import moment from "moment";
import { ClassroomDetails } from "@/stores/classroomStore_I";
import useStore from "@/stores";
import { getClassroomStore } from "stores/classroomStore";

/** Given an endTime in integer format convert it to number of seconds from now (which corresponds to classexpiry) */
export const convertEndTimeToSecsTillExpiry = (endTimeInt: number): number => {
	const currentDateTime = new Date();
	const currentMinute = currentDateTime.getMinutes();
	const currentHour = currentDateTime.getHours();

	const endTimeHour = Math.floor(endTimeInt / 100);
	const endTimeMinute = endTimeInt % 100;

	return (endTimeHour - currentHour) * 3600 + (endTimeMinute - currentMinute) * 60;
};

/**
 * Converts a unix timestamp to a formatted time string with proper zero-padding.
 * @param expiry - Unix timestamp in seconds, or undefined/null for no expiry
 * @returns Formatted time string or null if expiry is falsy
 */
const formatUnixTimeToString = (expiry?: number | null): string | null => {
	if (!expiry) return null;

	const unixMoment = moment.unix(expiry);
	let meridianTime = unixMoment.format("LT");

	// Add leading zero for single-digit hours
	if (meridianTime[1] === ":") {
		meridianTime = "0" + meridianTime;
	}

	return meridianTime;
};

/**
 * Generates a class expiry string based on the provided expiry time.
 * @param expiry
 * @param verbose
 * @param isMonitoring
 */
export const generateClassExpiryStr = (expiry: number, verbose = false, isMonitoring = false): string | null => {
	const meridianTime = formatUnixTimeToString(expiry);
	if (!meridianTime) return null;

	return verbose ? `${!isMonitoring ? "Class" : "Monitoring"} until ${meridianTime}` : meridianTime;
};

/**
 * Generates a class expiry string based on the provided expiry time for the tile on the overview page.
 * @param expiry - Unix timestamp in seconds, or undefined/null for no expiry
 */
export const generateClassEndsStr_tile = (expiry?: number | null): string | null => {
	const meridianTime = formatUnixTimeToString(expiry);
	if (!meridianTime) return null;

	return `Ends ${meridianTime}`;
};

/**
 * Generates session status text based on class end time and monitoring status.
 * @param classroomId - ID of the classroom
 * @param verbose - Whether to use verbose format
 */
export const generateSessionStatusTxt = (classroomId: string, verbose = false): string | null => {
	const classroomData = getClassroomStore(useStore.getState()).getClassroomV2(classroomId);
	const isMonitoring = classroomData.session?.isMonitoring;
	const classEndTime = getClassEndTimeUnix(classroomData);

	if (classEndTime === null) {
		return null;
	}

	return generateClassExpiryStr(classEndTime, verbose, isMonitoring);
};

/**
 * Determines the class end time based on the classroom details.
 * If the class is in monitoring mode, it checks the session end time.
 * If not in monitoring mode, it checks the session end time.
 * @param {ClassroomDetails} classroomDetails - The details of the classroom.
 * @returns {number | null} The class end time in unix format (seconds since epoch) or null if not found.
 */
export const getClassEndTimeUnix = (classroomDetails: ClassroomDetails): number | null => {
	const sessionEndTime = classroomDetails.session?.sessionEndTime;
	const isMonitoring = classroomDetails.session?.isMonitoring;
	if (isMonitoring) {
		if (sessionEndTime && sessionEndTime > new Date().getTime() / 1000) {
			return sessionEndTime;
		}
	} else {
		if (sessionEndTime && sessionEndTime > new Date().getTime() / 1000) {
			return sessionEndTime;
		}
	}
	return null;
};

/**
 * Determines the class start time based on the classroom details.
 * If the class is in monitoring mode, it checks the session start time.
 * If not in monitoring mode, it checks the session start time.
 * @param {ClassroomDetails} classroomDetails - The details of the classroom.
 * @returns {number | null} The class start time in unix format (seconds since epoch) or null if not found.
 */
export const getClassStartTimeUnix = (classroomDetails: ClassroomDetails): number | null => {
	if (classroomDetails !== undefined && Object.entries(classroomDetails).length > 0) {
		const currentTime = new Date().getTime() / 1000;
		const sessionStartTime = classroomDetails.session?.sessionStartTime;
		if (classroomDetails.session?.isMonitoring) {
			if (sessionStartTime && sessionStartTime < currentTime) {
				return sessionStartTime;
			}
			return null;
		} else {
			if (sessionStartTime && sessionStartTime < currentTime) {
				return sessionStartTime;
			}
		}
	}
	return null;
};
